import { Fragment, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Field, Form, withFormik } from 'formik';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { v4 as uuid } from 'uuid';

import API from 'utils/api';
import windowCheck from 'utils/helpers/window-check';

import ButtonGoogleSignIn from 'components/buttons/button-google-sign-in';
import Wysiwyg from 'components/content-blocks/partials/wysiwyg';
import globalSignupLegal from 'components/globals/content/signupLegal';
import FontIcon from 'components/icons';

import './styles/signup-full.scss';
import Button from '../buttons/button';
import SignupResponseError from '../modal/signup-response-error';
import {
  SignUpTrialLength,
} from './constants';
import WorkField from './fields/old-workfield';
import PhoneField from './fields/phone';
import handleSignupSubmit from './handlers/submit/handleSubmitSignup';
import handleValidateSignup from './handlers/validation/handleValidateSignup';

const fullNameRegExp = /\w+\s\w+/;
const phoneRegExp = /^\+?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})$/;
const emailRegExp = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

const SchedulingSignup = props => {
  const fullName = props.fullName || '';
  const email = props.email || '';
  const signupModalSubmit = props.signupModalSubmit || '';
  const legalText = props.legalText || '';
  const fieldLayout = props.fieldLayout || '';
  const showFields = props.showInitFields;
  const responseCode = props.responseCode || '';
  const targetModule = props.targetModule || '';
  const isZapier = props.isZapier || false;
  const isUniqueId = props.uniqueId !== '' ? props.uniqueId : uuid();
  const [responseNumber, setResponseNumber] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [claimedNumber, setClaimedNumber] = useState(0);
  const [hideErrorMessage, setHideErrorMessage] = useState(true);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [bypassDuplicateWorkplace, setBypassDuplicateWorkplace] = useState(false);

  const closeErrResponse = () => {
    setResponseNumber(0);
    setCreatingAccount(false);
  };

  const duplicateWorkplace = () => {
    setResponseNumber(0);
    setBypassDuplicateWorkplace(true);
  };

  const ldClient = useLDClient();
  const breakpoints = useBreakpoint();

  return (
    <div className="signup-form is-fullwidth">
      <FormikSchedulingSignup
        validateOnBlur={ false }
        validateOnChange={ false }
        fullName={ fullName }
        email={ email }
        signupModalSubmit={ signupModalSubmit }
        legalText={ legalText }
        fieldLayout={ fieldLayout }
        showInitFields={ showFields }
        responseCode={ responseCode }
        hideErrorMessage={ hideErrorMessage }
        setHideErrorMessage={ setHideErrorMessage }
        updateResponse={ setResponseNumber }
        updateFormValues={ setFormValues }
        updateClaimedNumber={ setClaimedNumber }
        bypassDuplicateWorkplace={ bypassDuplicateWorkplace }
        isZapier={ isZapier }
        updateCreatingAccount={ setCreatingAccount }
        creatingAccount={ creatingAccount }
        targetModule={ targetModule }
        uniqueId={ isUniqueId }
        refPlanId={ props.refPlanId }
        lockPlanId={ props.lockPlanId }
        trialLength={ props.trialLength }
        onSuccessfulSignup={ props.onSuccessfulSignup }
        ldClient={ ldClient }
        breakpoints={ breakpoints }
        title={ props.title }
        ctaVariation={ props.ctaVariation }
        isFixed={ props.isFixed }
      />
      { responseNumber > 0 && <SignupResponseError
        closeErrResponse={ closeErrResponse }
        responseNumber={ responseNumber }
        className='is-block'
        uniqueId={ isUniqueId }
        formValues={ formValues }
        claimedNumber={ claimedNumber }
        duplicateWorkplace={ duplicateWorkplace }
        bypassDuplicateWorkplace={ bypassDuplicateWorkplace }
      /> }
    </div>
  );
};

// Form
const AccountSchedulingSignup = ({
  values,
  errors,
  touched,
  setErrors,
  setHideErrorMessage,
  hideErrorMessage,
  signupModalSubmit,
  legalText,
  showInitFields,
  creatingAccount,
  uniqueId,
  handleSubmit,
  title,
  ...props
}) => {
  const timerReff = useRef(null);
  const ldClient = useLDClient();

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      // if there are errors show them
      setHideErrorMessage(false);
      setTimer();
    }
  }, [errors]);

  useEffect(() => {
    setHideErrorMessage(true); // on input value change hide the error message
    stopTimer();
  }, [values]);

  useEffect(() => {
    emailRegExp.test(values.email) && errors.email ? setErrors({}) : '';
    fullNameRegExp.test(values.fullName) && errors.fullName
      ? setErrors({})
      : '';
    phoneRegExp.test(values.phone) && errors.phone ? setErrors({}) : '';
    values.company && errors.company ? setErrors({}) : '';
    values.ref_employees && errors.ref_employees ? setErrors({}) : '';
  }, [values]);

  useEffect(() => {
    if (props.bypassDuplicateWorkplace) {
      handleSubmit();
    }
  }, [props.bypassDuplicateWorkplace]);

  useEffect(() => {
    // clear any phone values if we are hiding the phone field
    if (Number(values.ref_employees) <= 20) {
      values.phone = '';
    }

  }, [values.ref_employees]);

  function setTimer() {
    if (Object.keys(errors).length > 0) {
      timerReff.current = setTimeout(() => {
        setHideErrorMessage(true);
      }, 3000);
    }
  }

  function handleClick() {
    if (showInitFields) {
      ldClient.track('clickedSignupTwoPartForm');
    }
    if (timerReff.current) {
      stopTimer();
    }
    if (Object.keys(errors).length > 0) {
      setHideErrorMessage(false);
      setTimer();
    }
  }

  function blurHandler() {
    // on blur of input with current error message hide message
    setHideErrorMessage(true);
    if (timerReff.current) {
      stopTimer();
    }
  }

  function stopTimer() {
    clearTimeout(timerReff.current);
  }

  const renderPhoneField = () => {
    return <Fragment>
      <label htmlFor="phone">Mobile Number</label>
      <div className="control has-icons-left has-icons-right">
        <Field
          className={ classnames(['phone', { 'has-error': touched.phone && errors.phone }]) }
          title="Phone Number"
          aria-placeholder="Please enter your phone number"
          aria-required="true"
          component={ PhoneField }
          type="tel"
          id="phone"
          name="phone"
          onBlur={ blurHandler }
          placeholder="Get texts when your team views the schedule."
        />
        <span className="icon is-left">
          <FontIcon icon="phone" />
        </span>
        {touched.phone && errors.phone && (
          <div
            className={ classnames(['errors-tooltip', { 'is-hidden': hideErrorMessage }]) }
          >
            <p className="tooltip-inner has-text-danger">
              <span className="has-text-warning tooltip-icon">
                <FontIcon icon="warning" />
              </span>
              <span className="tooltip-text">{errors.phone}</span>
            </p>
          </div>
        )}
      </div>
    </Fragment>;
  };

  return (
    <Fragment>
      <Form 
        className="signup-full"
        id={ uniqueId }
        noValidate
        onSubmit={ handleSubmit }>
        {title && (
          <header className="has-text-centered">
            <img
              className="logo"
              src="https://marketing-assets.wheniwork-production.com/2019/08/15163825/logo.svg"
              alt="When I Work Logo"
            />
            <h2 className="title">{ creatingAccount ? `Creating your account for ${values.company}.` : title }</h2>
          </header>
        )}
        {creatingAccount && (
          <img
            src="https://marketing-assets.wheniwork-production.com/2021/12/10161313/animation_500_kx0y1v9k.gif"
            role="presentation"
          />
        )}
        <div className={ classnames(['mb-5', { 'is-hidden': creatingAccount }]) }>
          { (!showInitFields || props.isFixed) &&
            <Fragment>
              <ButtonGoogleSignIn ctaVariation={ props.ctaVariation } numSteps={ showInitFields ? 2 : 1 } />
              <div className="or-separator"><span>OR</span></div>
            </Fragment>
          }
          <label
            htmlFor={ `fullName${uniqueId}` }
            className={ classnames([{ 'is-hidden': showInitFields }]) }
          >
            Full Name
          </label>
          <div className={ classnames(['control', { 'is-hidden': showInitFields }]) }>
            <Field
              className={ classnames(['input full-name hasInputIcon', { 'has-error': touched.fullName && errors.fullName }]) }
              title="Full Name"
              id={ `fullName${uniqueId}` }
              type="text"
              name="fullName"
              aria-placeholder="Please enter your first and last name"
              aria-required="true"
              onBlur={ blurHandler }
              placeholder="First & Last Name"
            />
            <span className="icon is-small is-left inputIcons">
              <FontIcon icon="employee" />
            </span>
            {touched.fullName && errors.fullName && (
              <div
                className={ classnames(['errors-tooltip has-icons-left', { 'is-hidden': hideErrorMessage }]) }
              >
                <p className="tooltip-inner has-text-danger">
                  <span className="has-text-warning tooltip-icon">
                    <FontIcon icon="warning" />
                  </span>
                  <span className="tooltip-text">{errors.fullName}</span>
                </p>
              </div>
            )}
          </div>
          <label
            htmlFor={ `email${uniqueId}` }
            className={ showInitFields ? 'is-hidden' : '' }
          >
            Email
          </label>
          <div className={ classnames(['control', { 'is-hidden': showInitFields }]) }>
            <Field
              className={ classnames(['input email hasInputIcon', { 'has-error': touched.email && errors.email }]) }
              title="email"
              aria-placeholder="Please enter your email address"
              aria-required="true"
              id={ `email${uniqueId}` }
              type="email"
              name="email"
              onBlur={ blurHandler }
              placeholder="email@address.com"
            />
            <span className="icon is-small is-left inputIcons">
              <FontIcon icon="email" />
            </span>
            {touched.email && errors.email && (
              <div
                className={ classnames(['errors-tooltip', { 'is-hidden': hideErrorMessage }]) }
              >
                <p className="tooltip-inner has-text-danger">
                  <span className="has-text-warning tooltip-icon">
                    <FontIcon icon="warning" />
                  </span>
                  <span className="tooltip-text">{errors.email}</span>
                </p>
              </div>
            )}
          </div>

          <label htmlFor="company">Company Name</label>
          <div className="control has-icons-left">
            <Field
              className={ classnames(['input work', { 'has-error': touched.company && errors.company }]) }
              title="Company Name"
              aria-placeholder="Please enter your company name"
              aria-required="true"
              component={ WorkField }
              type="text"
              name="company"
              onBlur={ blurHandler }
              placeholder="Where do you work? (e.g. ACME Inc.)"
            />
            <span className="icon is-small is-left">
              <FontIcon icon="workplace" />
            </span>
            {touched.company && errors.company && (
              <div
                className={ classnames(['errors-tooltip', { 'is-hidden': hideErrorMessage }]) }
              >
                <p className="tooltip-inner has-text-danger">
                  <span className="has-text-warning tooltip-icon">
                    <FontIcon icon="warning" />
                  </span>
                  <span className="tooltip-text">{errors.company}</span>
                </p>
              </div>
            )}
          </div>
          <label htmlFor={ `ref_employees${uniqueId}` }>
            Number of Employees
          </label>
          <div className="control has-icons-left">
            <div className="select is-full-width">
              <Field
                as="select"
                className={ classnames([
                  'input select-field is-fullwidth',
                  {
                    'is-placeholder':
                      !values.ref_employees || values.ref_employees === '0',
                    'has-error': touched.ref_employees && errors.ref_employees,
                  },
                ]) }
                component="select"
                title="Employee Selection"
                aria-placeholder="Please enter how many employees you schedule?"
                aria-required="true"
                id={ `ref_employees${uniqueId}` }
                type="select"
                name="ref_employees"
                onBlur={ blurHandler }
                placeholder="How many people do you schedule?"
              >
                <option default value="0">
                  How many people do you schedule?
                </option>
                <option value="20">1 - 20</option>
                <option value="50">21 - 50</option>
                <option value="200">51 - 200</option>
                <option value="500">201 - 500</option>
                <option value="3000">501 - 3,000</option>
                <option value="3001">3,001+</option>
              </Field>
              <span className="icon is-small is-left">
                <FontIcon icon="employees" />
              </span>
            </div>
            {touched.ref_employees && errors.ref_employees && (
              <div
                className={ classnames(['errors-tooltip', { 'is-hidden': hideErrorMessage }]) }
              >
                <p className="tooltip-inner has-text-danger">
                  <span className="has-text-warning tooltip-icon">
                    <FontIcon icon="warning" />
                  </span>
                  <span className="tooltip-text">{errors.ref_employees}</span>
                </p>
              </div>
            )}
          </div>

          {renderPhoneField()}

          <Field type="hidden" aria-hidden="true" name="address" id="address" />
          <Field type="hidden" aria-hidden="true" name="street_name" id="street_name" />
          <Field type="hidden" aria-hidden="true" name="street_number" id="street_number" />
          <Field type="hidden" aria-hidden="true" name="locality" id="locality" />
          <Field type="hidden" aria-hidden="true" name="sub_locality" id="sub_locality" />
          <Field type="hidden" aria-hidden="true" name="region" id="region" />
          <Field type="hidden" aria-hidden="true" name="postal_code" id="postal_code" />
          <Field type="hidden" aria-hidden="true" name="country" id="country" />
          <Field type="hidden" aria-hidden="true" name="latitude" id="latitude" />
          <Field type="hidden" aria-hidden="true" name="longitude" id="longitude" />
          <Field type="hidden" aria-hidden="true" name="place_type" id="place_type" />
          <Field type="hidden" aria-hidden="true" name="place_id" id="place_id" />
          <Field type="hidden" aria-hidden="true" name="formatted_phone_number" id="formatted_phone_number" />
          <Field type="hidden" aria-hidden="true" name="international_phone_number" id="international_phone_number" />
          <Field type="hidden" aria-hidden="true" name="website" id="website" />
          <Field
            type="hidden"
            aria-hidden="true"
            name="ref_page"
            value={ windowCheck ? window.currentLocation : '' }
            id="ref_page"
          />
          <Field
            type="hidden"
            name="ref_plan_id"
            aria-hidden="true"
            value={ values.ref_plan_id || '' }
            id="ref_plan_id"
          />
        </div>
        <Button
          className={ classnames(['button is-primary is-fullwidth has-text-weight-bold', { 'is-hidden': creatingAccount && showInitFields }]) }
          type="submit"
          text={ creatingAccount ? 'Creating Account' : signupModalSubmit }
          style={
            creatingAccount
              ? { backgroundColor: '#d5d5d5', color: 'rgba(51, 51, 51, .7)' }
              : null
          }
          onClick={ handleClick }
        />
      </Form>
      <Wysiwyg content={ legalText !== '' ? legalText : globalSignupLegal() } /> 
    </Fragment>
  );
};

// Form Logic
const FormikSchedulingSignup = withFormik({
  mapPropsToValues({
    fullName,
    email,
    phone,
    ref_employees,
    company,
    address,
    street_name,
    street_number,
    locality,
    sub_locality,
    region,
    postal_code,
    country,
    latitude,
    longitude,
    place_type,
    place_id,
    formatted_phone_number,
    international_phone_number,
    website,
    ref_page,
    refPlanId,
    trialLength,
  }) {
    return {
      fullName: fullName || '',
      email: email || '',
      phone: phone || '',
      ref_employees: ref_employees || '',
      company: company || '',
      address: address || '',
      street_name: street_name || '',
      street_number: street_number || '',
      locality: locality || '',
      sub_locality: sub_locality || '',
      region: region || '',
      postal_code: postal_code || '',
      country: country || '',
      latitude: latitude || '',
      longitude: longitude || '',
      place_type: place_type || '',
      place_id: place_id || '',
      formatted_phone_number: formatted_phone_number || '',
      international_phone_number: international_phone_number || '',
      website: website || '',
      ref_page: ref_page || '',
      trialLength: trialLength || SignUpTrialLength.DEFAULT_TRIAL_LENGTH,
      ref_plan_id: refPlanId || '',
    };
  },
  validate: values => {
    const optionalFields = [];
    if (Number(values.ref_employees) <= 20) {
      optionalFields.push('phone');
    }
    
    return handleValidateSignup(values, fullNameRegExp, phoneRegExp, emailRegExp, optionalFields);
  },
  handleSubmit: (values, { props }) => {
    props.updateCreatingAccount(true);
    const checkPlaceEndpoint = () => API.checkPlace(values).then(results => {
      const result = results.data;
      if (result.claimed) {
        props.updateClaimedNumber(result.count);
        props.updateResponse(2721);
        props.updateFormValues(values);
      } else {
        handleSignupSubmit(values, props);
      }
    });

    if (
      typeof values.place_id !== 'undefined' &&
      values.place_id !== '' &&
      !props.bypassDuplicateWorkplace
    ) {
      return checkPlaceEndpoint();
    }

    return handleSignupSubmit(values, props);
  },
})(AccountSchedulingSignup);

export default SchedulingSignup;
